import { useEffect, useRef, useState } from 'react';

import { useAccessToken } from '@/services/auth';
import {
  acquireEmbryoEditLock,
  EmbryoEditLockConflictError,
  type EmbryoEditLockHolder,
  OnFarmImplantRequestError,
  releaseEmbryoEditLock,
  renewEmbryoEditLock,
  type RowVersions,
} from '@/services/on-farm-implant';

const HEARTBEAT_MS = 30_000;
const IDLE_TIMEOUT_MS = 10 * 60_000;
const ACTIVITY_EVENTS = ['keydown', 'pointerdown', 'input'] as const;

const ownLockIds = new Set<string>();

export function isOwnEmbryoEditLock(lockId: string | null | undefined) {
  return !!lockId && ownLockIds.has(lockId);
}

export type EmbryoEditLockState =
  | { status: 'idle' | 'acquiring' }
  | { status: 'held'; lockId: string; versions: RowVersions }
  | { status: 'conflict'; holders: EmbryoEditLockHolder[] }
  | { status: 'lost' }
  | { status: 'timedOut' }
  | { status: 'error'; error: unknown };

export function useEmbryoEditLock({
  sessionId,
  embryoIds,
  enabled,
  attempt = 0,
}: {
  sessionId: string;
  embryoIds: string[];
  enabled: boolean;
  attempt?: number;
}): EmbryoEditLockState {
  const token = useAccessToken();
  const tokenRef = useRef(token);

  useEffect(() => {
    tokenRef.current = token;
  }, [token]);

  const embryoKey = [...embryoIds].sort().join(',');
  const requestKey = `${sessionId}|${embryoKey}|${attempt}`;
  const [result, setResult] = useState<{
    key: string;
    state: EmbryoEditLockState;
  } | null>(null);
  const lastActivityRef = useRef(0);

  useEffect(() => {
    if (!enabled || !embryoKey) {
      return;
    }

    const setState = (state: EmbryoEditLockState) =>
      setResult({ key: requestKey, state });

    let cancelled = false;
    let lockId: string | null = null;
    let heartbeat: ReturnType<typeof setInterval> | undefined;

    const release = (keepalive = false) => {
      clearInterval(heartbeat);
      window.removeEventListener('pagehide', onPageHide);

      if (lockId) {
        const id = lockId;
        lockId = null;
        ownLockIds.delete(id);
        void releaseEmbryoEditLock(tokenRef.current, id, { keepalive }).catch(
          (error) => console.warn('Failed to release the edit lock', error),
        );
      }
    };

    const onPageHide = () => release(true);

    const onActivity = () => {
      lastActivityRef.current = Date.now();
    };

    const beat = async () => {
      if (Date.now() - lastActivityRef.current >= IDLE_TIMEOUT_MS) {
        release();
        setState({ status: 'timedOut' });
        return;
      }

      if (!lockId) {
        return;
      }

      try {
        await renewEmbryoEditLock(tokenRef.current, lockId);
      } catch (error) {
        if (cancelled) {
          return;
        }

        // Only a 409 means the lock is gone; other failures retry on the next
        // beat while the server lease is still valid.
        if (
          !(error instanceof OnFarmImplantRequestError && error.status === 409)
        ) {
          console.warn('Failed to renew the embryo edit lock', error);
          return;
        }

        clearInterval(heartbeat);
        ownLockIds.delete(lockId);
        lockId = null;
        setState({ status: 'lost' });
        console.warn('Lost the embryo edit lock', error);
      }
    };

    lastActivityRef.current = Date.now();
    ACTIVITY_EVENTS.forEach((type) =>
      document.addEventListener(type, onActivity, true),
    );

    acquireEmbryoEditLock(tokenRef.current, sessionId, embryoKey.split(','))
      .then((acquired) => {
        lockId = acquired.lockId;
        ownLockIds.add(acquired.lockId);

        if (cancelled) {
          release();
          return;
        }

        window.addEventListener('pagehide', onPageHide);
        heartbeat = setInterval(beat, HEARTBEAT_MS);
        setState({
          status: 'held',
          lockId: acquired.lockId,
          versions: acquired.versions,
        });
      })
      .catch((error: unknown) => {
        if (cancelled) {
          return;
        }

        setState(
          error instanceof EmbryoEditLockConflictError
            ? { status: 'conflict', holders: error.holders }
            : { status: 'error', error },
        );
      });

    return () => {
      cancelled = true;
      ACTIVITY_EVENTS.forEach((type) =>
        document.removeEventListener(type, onActivity, true),
      );
      release();
    };
  }, [enabled, embryoKey, sessionId, requestKey]);

  if (!enabled || !embryoKey) {
    return { status: 'idle' };
  }

  return result?.key === requestKey ? result.state : { status: 'acquiring' };
}
